import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import { Card, CardContent, Divider, IconButton, InlineMessage, Spinner, Tag, Typography } from '@volvo/vcdk-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type { IIndividualProduct } from '@/service/vpc/types/IIndividualProducts';
import type { IBusinessPartner, IOperation } from '@/service/vpc/types/IServiceProtocol';
import type { ColumnDef } from '@tanstack/react-table';

import EmptyState from '@/components/EmptyState';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import PageHeader from '@/components/pageheaders/PageHeader';
import Table from '@/components/Table';
import { loaderUnitMaintenanceProtocolPage } from '@/loaders/unit.maintenance.protocol.loader';
import {
  generateServiceTitle,
  getServiceProtocolChassisPosition,
  getType,
} from '@/routes/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/maintenance/_utils/serviceProtocol';
import {
  getIndividualProductsQueryOptions,
  getServiceProtocolByKeyQueryOptions,
  getServiceProtocolsQueryOptions,
  unitByIdQueryOptions,
} from '@/service/vpc/queryOptions';
import { chassis, dates } from '@/utils';

type ProtocolDetails = {
  title: string;
  currentService: string | number;
  previousService: string | number;
};

export function SingelServiceProtocolPage() {
  const { fleetId, endCustomerUnitId, protocolKey } = useParams({
    strict: false,
  });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data: unit, isLoading: isLoadingUnit } = useQuery(
    unitByIdQueryOptions({ endCustomerUnitId: endCustomerUnitId! })
  );

  const serialNumbers = chassis.getUnitSerialNumbers(unit);
  const chassisIdentifiers = chassis.getUnitsChassisIdentifier(unit) || [];

  const { data: protocol, isLoading: isLoadingProtocol } = useQuery(
    getServiceProtocolByKeyQueryOptions({ protocolKey: protocolKey! })
  );
  const { data: serviceProtocols } = useQuery(getServiceProtocolsQueryOptions({ chassisIdentifiers }));
  const { data: products } = useQuery(getIndividualProductsQueryOptions({ serialNumbers: serialNumbers || [] }));

  const calculateUnitAgeInMonth = (serviceDate: string, date: string) => {
    if (!date || !serviceDate) return '-';
    if (date === '0001-01-01T00:00:00' || serviceDate === '0001-01-01T00:00:00') return '-';

    const inOperationDate = new Date(date);
    const serviceDateObj = new Date(serviceDate);
    if (isNaN(inOperationDate.getTime()) || isNaN(serviceDateObj.getTime())) return '-';

    // return number of months between the two dates
    return (
      (serviceDateObj.getFullYear() - inOperationDate.getFullYear()) * 12 +
      (serviceDateObj.getMonth() - inOperationDate.getMonth()) +
      ' ' +
      t(($) => $.components.table.maintenance_protocol_details_table.months)
    );
  };

  const calculateLastPreformed = (operationDescription: string, taskType: string) => {
    if (!serviceProtocols || !protocol) return null;

    const currentServiceDate = new Date(protocol.serviceDate);

    // exclude current protocol and only include protocols older than the current one
    const serviceProtocolsSorted = serviceProtocols
      .filter((p) => p.protocolKey !== protocolKey && new Date(p.serviceDate) < currentServiceDate)
      .sort((a, b) => new Date(b.serviceDate).getTime() - new Date(a.serviceDate).getTime());

    // find the last service protocol where the serviceActivityEventDetails
    // contains an event with the same operationDescription and taskType and operationStatus is 'Performed'
    const lastPreformedProtocol = serviceProtocolsSorted.find((p) =>
      p.serviceActivityEventDetails.some((event) =>
        event.operationDetails.some(
          (op) =>
            op.operationDescription === operationDescription &&
            op.task.taskType === taskType &&
            op.operationStatus === 'Performed'
        )
      )
    );

    if (!lastPreformedProtocol) return null;

    return {
      protocolKey: lastPreformedProtocol.protocolKey,
      date: dates.formatDate(lastPreformedProtocol.serviceDate),
    };
  };

  const serviceProtocolDetails = (): ProtocolDetails[] => {
    if (!protocol) return [];
    return [
      {
        title: t(($) => $.components.table.maintenance_protocol_details_table.date),
        currentService: dates.formatDateShort(protocol.serviceDate),
        previousService: dates.formatDateShort(protocol.lastServiceDate),
      },
      {
        title: t(($) => $.components.table.maintenance_protocol_details_table.operating_hours),
        currentService: protocol.currentEngineHours,
        previousService: protocol.lastServiceEngineHours,
      },
      {
        title: t(($) => $.components.table.maintenance_protocol_details_table.unit_age),
        currentService: calculateUnitAgeInMonth(protocol.serviceDate, protocol.productInOperation),
        previousService: calculateUnitAgeInMonth(protocol.lastServiceDate, protocol.productInOperation),
      },
    ];
  };

  const columnsDealership = [
    {
      accessorFn: (row) => row.businessPartnerName,
      id: 'businessPartnerName',
      header: t(($) => $.components.table.maintenance_dealer_table.dealer_name),
      cell: (info) => {
        return <Typography variant="caption1">{info.getValue<string>()}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.businessPartnerLocation,
      id: 'businessPartnerLocation',
      header: t(($) => $.components.table.maintenance_dealer_table.location),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
  ] as ColumnDef<IBusinessPartner>[];

  const columnsProtocolDetails = [
    {
      accessorFn: (row) => row.title,
      id: 'title',
      header: t(($) => $.components.table.maintenance_protocol_details_table.title),
      cell: (info) => {
        return <Typography variant="caption1">{info.getValue<string>()}</Typography>;
      },
    },
    {
      accessorFn: (row) => row.currentService,
      id: 'currentService',
      header: t(($) => $.components.table.maintenance_protocol_details_table.current_service),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.previousService,
      id: 'previousService',
      header: t(($) => $.components.table.maintenance_protocol_details_table.previous_service),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
  ] as ColumnDef<ProtocolDetails>[];

  const columnsDriveline = [
    {
      accessorFn: (row) => row.productCategory,
      id: 'productCategory',
      header: t(($) => $.components.table.maintenance_driveline_table.name),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>()}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.productName,
      id: 'productName',
      header: t(($) => $.components.table.maintenance_driveline_table.product),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
    {
      accessorFn: (row) => row.serialNumber,
      id: 'serialNumber',
      header: t(($) => $.components.table.maintenance_driveline_table.serial_number),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
  ] as ColumnDef<IIndividualProduct>[];

  const columnsService = [
    {
      accessorFn: (row) => row.operationDescription,
      id: 'taskDesctiption',
      header: t(($) => $.components.table.maintenance_service_table.activity),
      cell: (info) => {
        const row = info.row.original;
        return (
          <div className="flex flex-col gap-2">
            <Typography variant="caption1" className="text-subtle">
              {info.getValue<string>()}
            </Typography>
            {!!row.operationComment && (
              <Typography variant="caption2" className="text-subtle">
                {row.operationComment}
              </Typography>
            )}
          </div>
        );
      },
    },
    {
      accessorFn: (row) => row.task.taskDesctiption,
      id: 'detail',
      header: t(($) => $.components.table.maintenance_service_table.detail),
      cell: (info) => {
        return (
          <Typography variant="caption1" className="text-subtle">
            {info.getValue<string>() ?? '-'}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'lastPerformed',
      id: 'lastPerformed',
      header: t(($) => $.components.table.maintenance_service_table.last_performed),
      cell: (info) => {
        const row = info.row.original as IOperation;
        const lastPerformed = calculateLastPreformed(row.operationDescription, row.task.taskType);

        if (!lastPerformed)
          return (
            <Typography variant="caption1" className="text-subtle">
              -
            </Typography>
          );

        return (
          <Link
            resetScroll
            to={
              fleetId
                ? '/fleets/$fleetId/units/$endCustomerUnitId/maintenance/$protocolKey'
                : '/units/$endCustomerUnitId/maintenance/$protocolKey'
            }
            params={{
              fleetId: fleetId || '',
              endCustomerUnitId: endCustomerUnitId || '',
              protocolKey: lastPerformed.protocolKey || '',
            }}
          >
            <Typography variant="caption1" className="text-subtle underline">
              {lastPerformed.date}
            </Typography>
          </Link>
        );
      },
    },
    {
      accessorFn: (row) => row.operationStatus,
      id: 'operationStatus',
      header: t(($) => $.components.table.maintenance_service_table.performed),
      cell: (info) => {
        const status = info.getValue<'NotSet' | 'NotPerformed' | 'Performed'>();
        return (
          <Tag variant={status === 'Performed' ? 'success-subtle' : 'neutral-subtle'}>
            {status === 'Performed'
              ? t(($) => $.components.table.maintenance_service_table.complete)
              : t(($) => $.components.table.maintenance_service_table.not_performed)}
          </Tag>
        );
      },
    },
  ] as ColumnDef<IOperation>[];

  // Scroll to top when protocol changes
  useEffect(() => {
    const mainElement = document.querySelector('main');
    if (mainElement) {
      mainElement.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [protocolKey]);

  if (isLoadingProtocol || isLoadingUnit) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <Spinner size="medium" className="" />
      </div>
    );
  }

  if (!protocol) {
    return <EmptyState title="" message="" />;
  }

  const preformedActivities = protocol.serviceActivityEventDetails.filter((detail) =>
    detail.operationDetails.some((op) => op.operationStatus === 'Performed')
  );

  const notPreformedActivities = protocol.serviceActivityEventDetails.filter((detail) =>
    detail.operationDetails.every((op) => op.operationStatus === 'NotSet' || op.operationStatus === 'NotPerformed')
  );

  const chassisPosition = getServiceProtocolChassisPosition(unit, protocol, t);
  const productCategorys = products?.map((product) => product.productCategory).join(', ');
  const serviceDate = dates.formatDate(protocol.serviceDate);

  const generateSubTitle = () => {
    let subTitle = '';
    if (chassisPosition) {
      subTitle += chassisPosition;
    }
    if (productCategorys) {
      subTitle += `${chassisPosition ? ', ' : ''}${productCategorys}`;
    }
    if (serviceDate) {
      subTitle += `${subTitle ? ' - ' : ''}${serviceDate}`;
    }
    return subTitle;
  };

  return (
    <>
      <PageHeader
        isLoading={isLoadingProtocol}
        title={generateServiceTitle(protocol, t)}
        subTitle={generateSubTitle()}
        items={[]}
        backButton={
          <IconButton
            icon="arrow-left"
            size="large"
            onClick={() =>
              fleetId
                ? navigate({
                    to: '/fleets/$fleetId/units/$endCustomerUnitId/maintenance',
                    params: { fleetId: fleetId, endCustomerUnitId: endCustomerUnitId! },
                  })
                : navigate({
                    to: '/units/$endCustomerUnitId/maintenance',
                    params: { endCustomerUnitId: endCustomerUnitId! },
                  })
            }
          />
        }
      />
      {unit?.claiming && (
        <InlineMessage icon="info" variant="info" className="mb-4">
          {t(($) => $.pages.unit.maintenance.unverified_ownership_notice)}
        </InlineMessage>
      )}
      <div className="flex flex-col gap-6">
        {protocol.businessPartner && (
          <Table
            isLoading={false}
            headerTitle={t(($) => $.components.table.maintenance_dealer_table.header)}
            data={[protocol.businessPartner]}
            columns={columnsDealership}
            showPagination={false}
            showSearch={false}
          />
        )}
        <Table
          isLoading={false}
          headerTitle={t(($) => $.components.table.maintenance_protocol_details_table.header)}
          data={serviceProtocolDetails()}
          columns={columnsProtocolDetails}
          showPagination={false}
          showSearch={false}
        />
        <Table
          isLoading={false}
          headerTitle={t(($) => $.components.table.maintenance_driveline_table.header, {
            driveline: `${protocol.chassisSeries || ''} ${protocol.chassisNumber || ''}`,
          })}
          data={
            products?.filter(
              (p) =>
                p.driveLineIdChassisNumber === String(protocol.chassisNumber) &&
                p.driveLineIdChassisSeries === String(protocol.chassisSeries)
            ) || []
          }
          columns={columnsDriveline}
          showPagination={false}
          showSearch={false}
        />
        {preformedActivities.map((event) => (
          <Table
            key={event.serviceActivityDescription}
            isLoading={false}
            headerTitle={t(($) => $.components.table.maintenance_service_table.header, {
              service: getType(event.serviceActivityDescription),
            })}
            data={event.operationDetails || []}
            columns={columnsService}
            showPagination={false}
            showSearch={false}
            actions={
              <>
                <Tag variant="info-subtle">
                  {`${
                    event.periodic
                      ? t(($) => $.pages.unit.maintenance.periodic_tag, {
                          months: event.months,
                          engineHours: event.engineHours,
                        })
                      : event.months
                        ? t(($) => $.pages.unit.maintenance.non_periodic_month_tag, { months: event.months })
                        : t(($) => $.pages.unit.maintenance.non_periodic_hours_tag, { engineHours: event.engineHours })
                  }`}
                </Tag>
              </>
            }
          />
        ))}
        <Card>
          <CardContent>
            <Typography variant="heading4">{t(($) => $.pages.unit.maintenance.general_comment)}</Typography>
            <Typography>{protocol.customProtocolComment || t(($) => $.pages.unit.maintenance.no_comment)}</Typography>
          </CardContent>
        </Card>
        <Divider />
        {notPreformedActivities.map((event) => (
          <Table
            key={event.serviceActivityDescription}
            isLoading={false}
            headerTitle={`Service ${getType(event.serviceActivityDescription)}`}
            data={event.operationDetails || []}
            columns={columnsService}
            showPagination={false}
            showSearch={false}
            actions={
              <>
                <Tag variant="info-subtle">
                  {`${
                    event.periodic
                      ? t(($) => $.pages.unit.maintenance.periodic_tag, {
                          months: event.months,
                          engineHours: event.engineHours,
                        })
                      : event.months
                        ? t(($) => $.pages.unit.maintenance.non_periodic_month_tag, { months: event.months })
                        : t(($) => $.pages.unit.maintenance.non_periodic_hours_tag, { engineHours: event.engineHours })
                  }`}
                </Tag>
              </>
            }
          />
        ))}
      </div>
    </>
  );
}

export const Route = createFileRoute(
  '/_authenticated/_account/fleets/$fleetId/units/$endCustomerUnitId/maintenance/$protocolKey/'
)({
  component: SingelServiceProtocolPage,
  errorComponent: ErrorBoundary,
  loader: loaderUnitMaintenanceProtocolPage,
});
